/**
 * <FittedImage> — une image à toute la largeur de son conteneur, à sa propre proportion (atom).
 *
 * Une fiche d'exercice photographiée, une figure : c'est l'énoncé, il se lit en grand, entier,
 * jamais recadré. `<MediaThumbnail>` est une vignette carrée de taille fixe ; celle-ci prend la
 * largeur qu'on lui donne et en déduit sa hauteur à partir des dimensions de l'image, lues une fois
 * chargée. En attendant, elle tient la place d'une page en paysage (`FALLBACK_RATIO`) : la hauteur
 * bouge au plus une fois, au premier rendu.
 *
 * Toucher l'image l'ouvre en grand : c'est l'appelant qui monte la visionneuse (`onPress`).
 */
import React, { useEffect, useState } from "react"
import { Image, Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

/** Une fiche en paysage, la forme la plus courante d'un énoncé photographié. */
const FALLBACK_RATIO = 4 / 3

export type FittedImageProps = {
    uri: string
    /** Toucher l'image : l'appelant l'ouvre en grand. Absent, l'image ne presse pas. */
    onPress?: () => void
    accessibilityLabel?: string
    /** La proportion lue (largeur / hauteur), une fois l'image chargée : pour qui borne sa hauteur. */
    onRatio?: (ratio: number) => void
    testID?: string
}

export function FittedImage(props: FittedImageProps) {
    const { uri, onPress, accessibilityLabel, onRatio, testID } = props
    const onRatioRef = React.useRef(onRatio)
    onRatioRef.current = onRatio
    const [ratio, setRatio] = useState(FALLBACK_RATIO)

    useEffect(() => {
        let mounted = true
        Image.getSize(
            uri,
            (width, height) => {
                if (!mounted || width <= 0 || height <= 0) return
                setRatio(width / height)
                onRatioRef.current?.(width / height)
            },
            () => {}
        )
        return () => {
            mounted = false
        }
    }, [uri])

    return (
        <Pressable
            onPress={onPress}
            disabled={onPress == null}
            accessibilityRole={onPress != null ? "imagebutton" : "image"}
            accessibilityLabel={accessibilityLabel}
            testID={testID}
        >
            <Image source={{ uri }} resizeMode="contain" style={styles.image(ratio)} />
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    image: (ratio: number) => ({
        width: "100%" as const,
        aspectRatio: ratio,
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    }),
}))
